<template>
  <b-modal
    ref="refModal"
    title="Auto completar pedido"
    size="xl"
    scrollable
    hide-footer
    @show="onShow"
    @hidden="onHidden"
  >
    <div v-if="clienteNome" class="mb-2">
      <small class="text-muted">
        Últimos itens comprados por
        <strong>{{ clienteNome }}</strong>
        <span v-if="representanteNome"> — representante <strong>{{ representanteNome }}</strong></span>
      </small>
    </div>

    <div v-if="!clienteId" class="text-center text-muted py-4">
      Selecione um cliente no pedido para ver os últimos itens comprados.
    </div>

    <b-overlay v-else :show="loading" rounded="sm">
      <!-- Filtros opcionais de pedido e data -->
      <b-row class="mb-2 gx-2">
        <b-col cols="12" md="4">
          <b-form-group label="Filtrar por Pedido (opcional)" class="mb-1">
            <b-input-group>
              <b-form-input
                v-model="filtroPedidoId"
                type="text"
                placeholder="Nº do pedido"
                :disabled="loading"
                @keyup.enter="buscar"
              />
              <b-input-group-append>
                <b-button variant="outline-primary" aria-label="Pesquisar por pedido" :disabled="loading" @click="buscar">
                  <feather-icon icon="SearchIcon" size="14" />
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="4">
          <b-form-group label="Filtrar por Data do Pedido (opcional)" class="mb-1">
            <b-input-group>
              <b-form-input
                v-model="filtroData"
                type="date"
                :disabled="loading"
              />
              <b-input-group-append>
                <b-button variant="outline-primary" aria-label="Pesquisar por data" :disabled="loading" @click="buscar">
                  <feather-icon icon="SearchIcon" size="14" />
                </b-button>
                <b-button variant="outline-secondary" aria-label="Limpar filtros" :disabled="loading" @click="limparFiltros">
                  <feather-icon icon="XIcon" size="14" />
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>
      </b-row>

      <p v-if="erroBusca" class="text-danger" role="alert">{{ erroBusca }}</p>
      <p v-else-if="filtrosAplicados && !loading" class="mb-1" role="status">
        <template v-if="filtrosAplicados.numeroPedido">Pedido pesquisado: <strong>{{ filtrosAplicados.numeroPedido }}</strong>.</template>
        <template v-else>Últimos pedidos do cliente.</template>
        <span v-if="filtrosAplicados.dataPedido"> Data: {{ filtrosAplicados.dataPedido }}.</span>
        <span> {{ linhas.length }} item(ns) encontrado(s).</span>
      </p>
      <p v-if="filtrosPendentes && !loading" class="text-warning">
        Filtro alterado: clique em pesquisar para atualizar os itens.
      </p>
      <div v-if="!loading && !erroBusca && linhas.length === 0" class="text-center text-muted py-4">
        Nenhum item encontrado nas compras anteriores deste cliente.
      </div>

      <b-table-simple v-else responsive hover small class="mb-0">
        <b-thead>
          <b-tr>
            <b-th style="width: 40px;">
              <b-form-checkbox
                :checked="todosSelecionados"
                :indeterminate="algunsSelecionados"
                @change="toggleTodos"
              />
            </b-th>
            <b-th>Pedido</b-th>
            <b-th>Data</b-th>
            <b-th>Referência</b-th>
            <b-th>Descrição</b-th>
            <b-th class="text-center" style="width: 110px;">Qtde</b-th>
          </b-tr>
        </b-thead>
        <b-tbody>
          <b-tr v-for="(linha, idx) in linhas" :key="idx">
            <b-td>
              <b-form-checkbox v-model="linha._selecionado" />
            </b-td>
            <b-td>{{ linha._pedidoId || '-' }}</b-td>
            <b-td>{{ linha._pedidoData || '-' }}</b-td>
            <b-td>{{ linha.familia_comercial_id }}</b-td>
            <b-td>{{ linha.descricao }}</b-td>
            <b-td class="text-center">
              <b-form-input
                v-model.number="linha._qtd"
                type="number"
                min="1"
                size="sm"
                class="text-center"
                @input="onQtdInput(linha, $event)"
              />
            </b-td>
          </b-tr>
        </b-tbody>
      </b-table-simple>
    </b-overlay>

    <div class="d-flex justify-content-end mt-3">
      <b-button variant="outline-secondary" class="mr-2" @click="fechar">
        Cancelar
      </b-button>
      <b-button
        variant="primary"
        :disabled="adicionando || loading || !!erroBusca || filtrosPendentes || quantidadeSelecionados === 0"
        @click="adicionar"
      >
        <b-spinner v-if="adicionando" small class="mr-1" />
        Adicionar ao pedido ({{ quantidadeSelecionados }})
      </b-button>
    </div>
  </b-modal>
</template>

<script>
import { simularImpostoUnitario } from '@/utils/impostoSimulador';
import { validarEstoqueItens } from '@/utils/estoqueBloqueio';
import { IMAGEM_PADRAO } from '@/utils/imagemUrl';
import { isPerfilClienteAtivo, isPerfilRepresentanteAtivo, nomeCategoria } from '@/libs/perfilCliente';
import axios from '@/libs/axios';
import Swal from 'sweetalert2';

const SLSAdocaoClient = require('@/api/Operacoes/sls-adocao.client').default;

export default {
  name: 'ModalAutoCompletarPedido',
  props: {
    // 'home' (carrinho do Portal B2B) ou 'operacao' (tela de criação de pedido)
    contexto: {
      type: String,
      default: 'home',
    },
  },
  data() {
    return {
      client: new SLSAdocaoClient(),
      loading: false,
      aberto: false,
      buscaSequencia: 0,
      filtrosAplicados: null,
      erroBusca: '',
      adicionando: false,
      linhas: [],
      ultimoClienteBuscado: null,
      filtroPedidoId: '',
      filtroData: '',
      precosBaseAtuais: {},
    };
  },
  computed: {
    formInfoPedido() {
      return this.$store.getters['InfoPedido/GET_FORM_INFO_PEDIDO'] || {};
    },
    clienteId() {
      return this.formInfoPedido.cliente && this.formInfoPedido.cliente.clienteid
        ? this.formInfoPedido.cliente.clienteid
        : null;
    },
    clienteNome() {
      const c = this.formInfoPedido.cliente || {};
      return c.clienterazaosocial || c.clientenomeabreviado || '';
    },
    representanteId() {
      return this.formInfoPedido.representante && this.formInfoPedido.representante.representanteid
        ? this.formInfoPedido.representante.representanteid
        : null;
    },
    representanteNome() {
      return this.formInfoPedido.representante ? this.formInfoPedido.representante.representantenome : '';
    },
    quantidadeSelecionados() {
      return this.linhas.filter((l) => l._selecionado && Number(l._qtd) > 0).length;
    },
    filtrosPendentes() {
      return !!this.filtrosAplicados && (
        (String(this.filtroPedidoId || '').trim() || null) !== this.filtrosAplicados.numeroPedido
        || (String(this.filtroData || '').trim() || null) !== this.filtrosAplicados.dataPedido
      );
    },
    todosSelecionados() {
      return this.linhas.length > 0 && this.linhas.every((l) => l._selecionado);
    },
    algunsSelecionados() {
      return this.linhas.some((l) => l._selecionado) && !this.todosSelecionados;
    },
  },
  watch: {
    clienteId(novo) {
      // Reativo: se o cliente mudar enquanto o modal está aberto, recarrega.
      if (this.$refs.refModal && this.aberto && novo !== this.ultimoClienteBuscado) {
        this.buscar();
      }
    },
  },
  methods: {
    chaveSku(item) {
      const valor = item || {};
      return [
        valor.item_id || valor.itemId || '',
        valor.cdn_cor || valor.cdncor || '',
        valor.cdn_tamanho || valor.desc_tamanho || valor.des_tamanho || '',
        valor.cdn_embalagem || valor.cdn_embalag || '',
      ].map(campo => String(campo).trim().toUpperCase()).join('|');
    },
    consolidarLinhas(itens, pedidoRef = null) {
      const consolidados = new Map();

      (itens || []).forEach((it) => {
        const chave = this.chaveSku(it);
        if (!chave || chave === '|||') return;

        const quantidade = Number(it._qtd != null ? it._qtd : it.quantidade);
        const pedidoId = it.pedidoid || it._pedidoIdOriginal
          || (pedidoRef && pedidoRef.pedidoid) || '';
        const pedidoData = it.pedidodata || it._pedidoDataOriginal
          || (pedidoRef && pedidoRef.pedidodata) || '';
        const existente = consolidados.get(chave);

        if (existente) {
          existente._qtd += quantidade > 0 ? quantidade : 1;
          if (pedidoId) existente._pedidos.add(String(pedidoId));
          if (pedidoData) existente._datas.add(String(pedidoData));
          existente._selecionado = existente._selecionado || it._selecionado !== false;
          return;
        }

        consolidados.set(chave, {
          ...it,
          _selecionado: it._selecionado !== false,
          _qtd: quantidade > 0 ? quantidade : 1,
          _pedidoIdOriginal: pedidoId,
          _pedidoDataOriginal: pedidoData,
          _pedidos: new Set(pedidoId ? [String(pedidoId)] : []),
          _datas: new Set(pedidoData ? [String(pedidoData)] : []),
        });
      });

      return Array.from(consolidados.values()).map((linha) => {
        const pedidos = Array.from(linha._pedidos);
        const datas = Array.from(linha._datas);
        return {
          ...linha,
          _pedidoId: pedidos.length > 1 ? `${pedidos.length} pedidos` : (pedidos[0] || ''),
          _pedidoData: datas.length > 1 ? 'Várias datas' : (datas[0] || ''),
          _pedidos: pedidos,
          _datas: datas,
        };
      });
    },
    open() {
      this.$refs.refModal.show();
    },
    fechar() {
      this.$refs.refModal.hide();
    },
    onShow() {
      this.aberto = true;
      this.buscar();
    },
    onHidden() {
      this.aberto = false;
      this.buscaSequencia += 1;
      this.loading = false;
    },
    limparFiltros() {
      this.filtroPedidoId = '';
      this.filtroData = '';
      this.buscar();
    },
    onQtdInput(linha, val) {
      // Garante que a quantidade seja sempre um inteiro positivo
      const n = parseInt(val, 10);
      if (!n || n < 1) {
        linha._qtd = 1;
      } else {
        linha._qtd = n;
      }
    },
    async buscar() {
      const sequencia = ++this.buscaSequencia;
      this.linhas = [];
      this.precosBaseAtuais = {};
      this.filtrosAplicados = null;
      this.erroBusca = '';
      if (!this.clienteId) {
        this.loading = false;
        return;
      }
      this.loading = true;
      this.ultimoClienteBuscado = this.clienteId;
      try {
        const pedidoidFiltro = String(this.filtroPedidoId || '').trim() || null;
        const dataFiltro = String(this.filtroData || '').trim() || null;
        const { data } = await this.client.getUltimosItensComprados(
          this.clienteId,
          this.representanteId,
          null,
          pedidoidFiltro,
          dataFiltro,
        );
        if (sequencia !== this.buscaSequencia) return;
        const payload = data || {};
        if ((pedidoidFiltro || dataFiltro) && (
          !payload.filtrosAplicados
          || payload.filtrosAplicados.numeroPedido !== pedidoidFiltro
          || payload.filtrosAplicados.dataPedido !== dataFiltro
        )) {
          throw new Error('A consulta não confirmou o filtro solicitado. Nenhum item foi carregado; atualize a página e tente novamente.');
        }
        const pedidoRef = payload.pedidoReferencia || null;
        // O histórico pode trazer o mesmo SKU em pedidos diferentes. Exiba e
        // processe uma única linha por SKU/variante, somando as quantidades.
        this.linhas = this.consolidarLinhas(payload.itens || [], pedidoRef);
        this.filtrosAplicados = { numeroPedido: pedidoidFiltro, dataPedido: dataFiltro };
      } catch (e) {
        if (sequencia !== this.buscaSequencia) return;
        const msg = (e && e.message) ? e.message : String(e);
        this.erroBusca = `Erro ao buscar itens: ${msg}`;
        this.$bvToast.toast(`Erro ao buscar itens: ${msg}`, {
          title: 'Auto completar pedido', variant: 'danger', solid: true,
        });
      } finally {
        if (sequencia === this.buscaSequencia) this.loading = false;
      }
    },
    tabelaPrecoIdAtual() {
      return (this.formInfoPedido.tabelaPreco && this.formInfoPedido.tabelaPreco.tabelapreco_id)
        || (this.formInfoPedido.dataCapaPedido && this.formInfoPedido.dataCapaPedido.tabelaprecoid)
        || null;
    },
    async buscarPrecoBaseAtual(itemId) {
      const tabelaPrecoId = this.tabelaPrecoIdAtual();
      if (!tabelaPrecoId || !itemId) return null;

      const cacheKey = `${tabelaPrecoId}:${itemId}`;
      if (Object.prototype.hasOwnProperty.call(this.precosBaseAtuais, cacheKey)) {
        return this.precosBaseAtuais[cacheKey];
      }

      let precoBase = null;
      try {
        const { data } = await axios.get(
          `tabela-preco-item/tabelapreco/${encodeURIComponent(tabelaPrecoId)}/item/${encodeURIComponent(itemId)}`,
        );
        const valor = Number(data && (
          data.tabelapreco_item_preco
          ?? data.tabelaprecoitempreco
          ?? data.preco
        ));
        precoBase = Number.isFinite(valor) && valor > 0 ? valor : null;
      } catch (e) {
        precoBase = null;
      }

      this.precosBaseAtuais[cacheKey] = precoBase;
      return precoBase;
    },
    /**
     * Busca o preço vigente na tabela atual e então recalcula o preço/imposto.
     * Nunca usa preço histórico do pedido como fallback.
     */
    async precoFinalItem(it) {
      const precoBase = await this.buscarPrecoBaseAtual(it.item_id);
      if (precoBase == null) return null;

      let preco = null;
      try {
        preco = await simularImpostoUnitario({
          formData: {
            ...this.formInfoPedido,
            enderecoAlternativo: this.$store.getters['InfoPedido/GET_ENDERECO_ALTERNATIVO'],
          },
          itemId: it.item_id,
          precoBase,
          comProveniencia: true,
        });
      } catch (e) {
        preco = null;
      }

      // Preço calculado deve ser positivo; se falhou, retorna null (não usa histórico).
      if (preco == null || Number(preco.preco) <= 0) {
        return null;
      }
      return {
        precoBase,
        precoFinal: Number(preco.preco),
        precoTabelaErp: preco.precoTabelaErp,
      };
    },
    async adicionar() {
      if (this.loading || this.erroBusca || this.filtrosPendentes) return;
      // Consolidação defensiva: estoque, preço e carrinho sempre recebem a
      // quantidade total do SKU, mesmo que uma origem futura repita linhas.
      const selecionados = this.consolidarLinhas(
        this.linhas.filter((l) => l._selecionado && Number(l._qtd) > 0),
      );
      if (selecionados.length === 0) return;
      this.adicionando = true;
      try {
        // Perfil Cliente: um pedido tem UMA categoria só (a do tipo de pedido
        // selecionado) — mesma regra da home (ModalEmbalagens) e do Novo Pedido.
        if (isPerfilClienteAtivo()) {
          const infoForm = this.$store.getters['InfoPedido/GET_FORM_INFO_PEDIDO'] || {};
          const tipoPedidoSelecionado = String(
            infoForm.tipoPedido?.tipopedidoid
              || infoForm.tipoPedidoCliente?.tipopedidoid
              || this.$store.getters['intensPedido/getFormData']?.dataCapaPedido?.tipopedidoid
              || '',
          ).toUpperCase();
          if (tipoPedidoSelecionado) {
            let flagsMap = {};
            try {
              const ids = [...new Set(selecionados.map((l) => l.item_id).filter(Boolean))];
              const { data: catResp } = await axios.post('items/categorias', {
                itemIds: ids,
                clienteid: infoForm.cliente?.clienteid,
              });
              flagsMap = catResp?.flags || {};
            } catch (e) {
              console.warn('[ModalAutoCompletarPedido] categorias dos itens indisponíveis:', e?.message);
            }
            const incompativel = selecionados.find((l) => {
              const flags = flagsMap[l.item_id];
              return Array.isArray(flags) && flags.length > 0 && !flags.includes(tipoPedidoSelecionado);
            });
            if (incompativel) {
              const flagsItem = flagsMap[incompativel.item_id] || [];
              Swal.fire({
                icon: 'info',
                title: 'Categoria diferente',
                text: `O produto "${incompativel.descricao || incompativel.item_id}" é da categoria ${nomeCategoria(flagsItem[0])}, `
                  + `diferente da categoria deste pedido (${nomeCategoria(tipoPedidoSelecionado)}). `
                  + 'Para incluí-lo, finalize este pedido e faça um novo pedido da categoria dele.',
              });
              this.adicionando = false;
              return;
            }
          }
        }
        // Bloqueio de venda sem estoque — mesma regra da home (ModalEmbalagens):
        // preferência ligada OU perfil Representante. O depósito segue o tipo
        // do pedido (doação WD → DOA; demais → LUI/LUN).
        const bloqueados = await validarEstoqueItens(
          selecionados.map((l) => ({ item_id: l.item_id, quantidade: Number(l._qtd) })),
        );
        if (bloqueados.size > 0) {
          const primeiro = selecionados.find((l) => bloqueados.has(String(l.item_id)));
          const info = bloqueados.get(String(primeiro.item_id));
          const detalhe = info && info.motivo === 'sem_estoque'
            ? (isPerfilClienteAtivo() || (isPerfilRepresentanteAtivo() && Number(info.disponivel) > 50)
              ? 'Estoque insuficiente.'
              : `Disponível: ${info.disponivel} un.`)
            : 'Não foi possível confirmar o estoque.';
          Swal.fire({
            icon: 'warning',
            title: 'Estoque insuficiente',
            text: `${bloqueados.size} item(ns) sem estoque disponível — ex.: "${primeiro.descricao || primeiro.item_id}" (${detalhe}) `
              + 'Desmarque o(s) item(ns) ou ajuste as quantidades para prosseguir.',
          });
          this.adicionando = false;
          return;
        }

        // Precifica cada item com preço ATUAL (via simulador de imposto).
        // Itens sem preço atual não são adicionados e geram aviso claro.
        const itensPreco = [];
        const itensSemPreco = [];
        for (const it of selecionados) {
          // eslint-disable-next-line no-await-in-loop
          const precificacao = await this.precoFinalItem(it);
          if (!precificacao || precificacao.precoFinal <= 0) {
            itensSemPreco.push(it.descricao || it.familia_comercial_id || it.item_id);
          } else {
            itensPreco.push({
              it,
              precoBase: precificacao.precoBase,
              precoFinal: precificacao.precoFinal,
              precoTabelaErp: precificacao.precoTabelaErp,
              qtd: Number(it._qtd),
            });
          }
        }

        if (itensSemPreco.length > 0) {
          Swal.fire({
            icon: 'warning',
            title: 'Preço atual indisponível',
            html: `Os seguintes itens não puderam ser adicionados porque não foi possível obter o preço atual:<br><b>${itensSemPreco.join('<br>')}</b><br><br>Verifique se a tabela de preço e as condições do pedido estão configuradas corretamente.`,
          });
          if (itensPreco.length === 0) {
            this.adicionando = false;
            return;
          }
        }

        if (this.contexto === 'operacao') {
          this.adicionarOperacao(itensPreco);
        } else {
          this.adicionarHome(itensPreco);
        }

        this.$bvToast.toast(`${itensPreco.length} item(ns) adicionado(s) ao pedido.`, {
          title: 'Auto completar pedido', variant: 'success', solid: true,
        });
        this.fechar();
      } catch (e) {
        const msg = (e && e.message) ? e.message : String(e);
        this.$bvToast.toast(`Erro ao adicionar itens: ${msg}`, {
          title: 'Auto completar pedido', variant: 'danger', solid: true,
        });
      } finally {
        this.adicionando = false;
      }
    },
    adicionarHome(itensPreco) {
      for (const {
        it, precoBase, precoFinal, precoTabelaErp, qtd,
      } of itensPreco) {
        const seq = this.$store.getters['PedidosStore/getLastSequenceItemPedido'] + 10;
        const product = {
          familia_comercial_id: it.familia_comercial_id,
          familia_comercial_descricao: it.descricao,
          cdn_cor: it.cdn_cor,
          cdn_tamanho: it.cdn_tamanho,
          des_tamanho: it.des_tamanho,
          des_cor: it.des_cor,
          item_id: it.item_id,
          // Foto real do produto (vinda do backend); sem ela o card do carrinho ficava sem imagem.
          item_imagem: it.item_img_principal || IMAGEM_PADRAO,
          item_descricao: `${it.familia_comercial_id} - ${it.descricao}`,
          pedido_item_seq: seq,
          pic_id: 0,
          marca: it.marca || '',
          embalagem: it.des_embalagem,
          cdn_embalag: it.cdn_embalagem,
          pedido_item_preco_unitario: precoFinal,
          pedido_item_preco_tabela_erp: precoTabelaErp,
          precoDoacaoErp: String(this.formInfoPedido?.tipoPedidoCliente?.tipopedidoid || '').toUpperCase() === 'WD' ? precoFinal : undefined,
          pedido_item_preco_unitario_base: precoBase,
          pedido_item_quantidade: qtd,
          valor_total: +(precoFinal * qtd).toFixed(2),
          pedido_item_preco_total: +(precoFinal * qtd).toFixed(2),
          precoComImposto: precoFinal,
          valorTotalComImposto: +(precoFinal * qtd).toFixed(2),
          naturezaoperacaoidimposto: null,
          cliente: (this.formInfoPedido.cliente || {}).clientenomeabreviado || '',
        };
        this.$store.commit('PedidosStore/ADD_OR_UPDATE_PRODUCT_ITEM_PEDIDO', product);
      }
    },
    toggleTodos(valor) {
      const marcar = valor === undefined ? !this.todosSelecionados : valor;
      this.linhas.forEach((l) => {
        l._selecionado = marcar;
        if (marcar && (!l._qtd || Number(l._qtd) <= 0)) {
          l._qtd = 1;
        }
      });
    },
    adicionarOperacao(itensPreco) {
      const tabelaprecoid = (this.formInfoPedido.tabelaPreco && this.formInfoPedido.tabelaPreco.tabelapreco_id)
        || (this.formInfoPedido.dataCapaPedido && this.formInfoPedido.dataCapaPedido.tabelaprecoid)
        || null;

      const grupos = {};
      let seqGrupo = 0;
      for (const { it, precoFinal, precoTabelaErp, qtd } of itensPreco) {
        const ref = it.familia_comercial_id;
        if (!grupos[ref]) {
          seqGrupo += 1;
          grupos[ref] = {
            CodMarca: null,
            DescricaoMarca: it.marca || '',
            CodReferencia: ref,
            DescricaoReferencia: it.descricao,
            referencia: `${ref}-${it.descricao}`,
            Qtde: 0,
            PrecoUnitario: precoFinal,
            ValorTotal: 0,
            PrecoComImposto: precoFinal,
            BaseIcms: 0,
            ValorIcms: 0,
            sequencia: seqGrupo,
            itens: [],
            editar: {
              cod_marca: null,
              cod_categ: null,
              cod_referencia: ref,
              precoId: tabelaprecoid,
            },
          };
        }
        const grupo = grupos[ref];
        grupo.Qtde += qtd;
        grupo.ValorTotal += +(precoFinal * qtd).toFixed(2);
        const subItemNovo = {
          codMarca: null,
          familiaComercialId: ref,
          cdncor: it.cdn_cor,
          des_cor: it.des_cor,
          cdn_tamanho: it.des_tamanho,
          itemId: it.item_id,
          item_descricao: `${it.descricao} ${it.des_tamanho || ''} ${it.des_cor || ''}`.trim(),
          descricao_referencia: it.descricao,
          pedidoItemQuantidade: qtd,
          marca: it.marca || '',
          embalagem: it.des_embalagem,
          cdn_embalagem: it.cdn_embalagem,
          precoUnitario: precoFinal,
          precoTabelaErp,
          valorTotal: +(precoFinal * qtd).toFixed(2),
          pedidoItemQuantidadeSaldo: 0,
          pedidoItemQuantidadeFaturada: 0,
          pedidoitemseq: 0,
          desc_tamanho: it.des_tamanho,
          precoComImposto: precoFinal,
          baseIcms: 0,
          valorIcms: 0,
          itemMultiplo: 1,
          naturezaoperacaoidimposto: null,
          valorTotalComImposto: +(precoFinal * qtd).toFixed(2),
        };
        const subItemExistente = grupo.itens.find(
          sub => this.chaveSku(sub) === this.chaveSku(subItemNovo),
        );
        if (subItemExistente) {
          const novaQtd = (Number(subItemExistente.pedidoItemQuantidade) || 0) + qtd;
          subItemExistente.pedidoItemQuantidade = novaQtd;
          subItemExistente.valorTotal = +((Number(subItemExistente.precoUnitario) || 0) * novaQtd).toFixed(2);
          subItemExistente.valorTotalComImposto = +((Number(subItemExistente.precoComImposto) || 0) * novaQtd).toFixed(2);
        } else {
          grupo.itens.push(subItemNovo);
        }
      }

      const listaAtual = this.$store.getters['intensPedido/getlistarTabelaItens'] || [];

      Object.keys(grupos).forEach((ref) => {
        const grupoNovo = grupos[ref];
        const existente = listaAtual.find(
          (g) => g.referencia === grupoNovo.referencia && g.CodReferencia === grupoNovo.CodReferencia,
        );

        if (!existente) {
          this.$store.dispatch('intensPedido/setListTabelaItens', grupoNovo);
          return;
        }

        const grupoMesclado = {
          ...existente,
          itens: (existente.itens || []).map((sub) => ({ ...sub })),
        };

        grupoNovo.itens.forEach((subNovo) => {
          const subExistente = grupoMesclado.itens.find(
            sub => this.chaveSku(sub) === this.chaveSku(subNovo),
          );
          if (subExistente) {
            const novaQtd = (Number(subExistente.pedidoItemQuantidade) || 0)
              + (Number(subNovo.pedidoItemQuantidade) || 0);
            subExistente.pedidoItemQuantidade = novaQtd;
            subExistente.valorTotal = +((Number(subExistente.precoUnitario) || 0) * novaQtd).toFixed(2);
            subExistente.valorTotalComImposto = +((Number(subExistente.precoComImposto) || 0) * novaQtd).toFixed(2);
          } else {
            grupoMesclado.itens.push({ ...subNovo });
          }
        });

        grupoMesclado.Qtde = grupoMesclado.itens.reduce(
          (acc, sub) => acc + (Number(sub.pedidoItemQuantidade) || 0),
          0,
        );
        grupoMesclado.ValorTotal = +grupoMesclado.itens.reduce(
          (acc, sub) => acc + (Number(sub.valorTotal) || 0),
          0,
        ).toFixed(2);

        this.$store.dispatch('intensPedido/setListTabelaItens', grupoMesclado);
      });
    },
  },
  created() {
    this.aberto = false;
  },
};
</script>
